import clsx from "clsx";
import { graphql, Link } from "gatsby";
import PropTypes from "prop-types";
import React, { ReactElement } from "react";

import { Container, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";

import Layout from "../components/Layout";
import TabPanels from "../components/TabPanels";

import Helmet from "react-helmet";
import PreviewCompatibleImage from "../components/PreviewCompatibleImage";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gridArea: "content"
    },
    header: {
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(8)
    },
    content: {
      display: "grid",
      [theme.breakpoints.down("sm")]: {
        gap: `${theme.spacing(8)}px`
      },
      [theme.breakpoints.up("sm")]: {
        gap: `${theme.spacing(16)}px`
      }
    },
    title: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    heading: {
      fontSize: "16pt",
      fontWeight: "bold",
      [theme.breakpoints.up("sm")]: {
        marginBottom: theme.spacing(4)
      }
    },
    subheading: {
      fontSize: "14pt",
      fontWeight: "bold"
    },
    description: {},
  })
);

export const ServicesPageTemplate = ({
  title,
  heading,
  featuredimage,
  imagewidth,
  subheading,
  description,
}): ReactElement => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <header>
        {/* <Typography component="h1" variant="h4" className={classes.title}>
          {title}
        </Typography> */}
        {heading && (
          <Typography
            className={clsx(classes.heading)}
            component="h1"
            variant="h2"
          >
            {heading}
          </Typography>
        )}
        {featuredimage ? (
          <PreviewCompatibleImage
            imageInfo={{
              image: featuredimage,
              alt: heading,
              style: {
                width: `${imagewidth}px`,
                minWidth: '370px',
                maxWidth: '1024px',
                margin: "1rem auto",
              }
            }}
          />
        ) : null}
        {subheading && (
          <Typography
            className={clsx(classes.subheading)}
            component="h2"
            variant="h3"
          >
            {subheading}
          </Typography>
        )}
        {description && (
          <Typography className={clsx(classes.description)} component="p">
            {description}
          </Typography>
        )}
      </header>
      <div className={classes.content}>
        <Container
          component="main"
          fluid="fluid"
          className={clsx(classes.segment, classes.main)}
        >
          {/* <TabPanels items={tabs} /> */}
        </Container>
      </div>
    </div>
  );
};

ServicesPageTemplate.propTypes = {
  logo: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  title: PropTypes.string,
  heading: PropTypes.string,
  featuredimage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imagewidth: PropTypes.string,
  subheading: PropTypes.string,
  description: PropTypes.string,
  helmet: PropTypes.object
};

const ServicesPage = ({ data, location }): ReactElement => {
  const { frontmatter } = data.markdownRemark;
  const rootUrl = "https://williamslynch.co.uk";

  return (
    <Layout pathname={location.pathname}>
      <ServicesPageTemplate
        title={frontmatter.title}
        heading={frontmatter.heading}
        subheading={frontmatter.subheading}
        description={frontmatter.description}
        featuredimage={frontmatter.featuredimage}
        imagewidth={frontmatter.imagewidth}
        helmet={
          <Helmet titleTemplate="%s">
            <title>{`${frontmatter.title}`}</title>
            <meta name="description" content={`${frontmatter.description}`} />
            <meta name="image" content={`${rootUrl}${frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="og:image" content={`${rootUrl}${frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="twitter:image" content={`${rootUrl}${frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
          </Helmet>
        }
      />
    </Layout>
  );
};

ServicesPage.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.object
    })
  }),
  location: PropTypes.object
};

export default ServicesPage;

export const pageQuery: IndexData = graphql`
  query ServicesPageTemplate {
    markdownRemark(frontmatter: { templateKey: { eq: "services-page" } }) {
      frontmatter {
        title
        heading
        subheading
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 960, quality: 64) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imagewidth
        description
      }
    }
  }
`;
